import { useState, useEffect } from 'react'
import moment from 'moment'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { Skeleton } from '@/components/ui/skeleton'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Users,
  UserPlus,
  UserCheck,
  Calendar,
  Download,
  RefreshCcw,
  ChevronDown,
  TrendingUp,
  Star,
  Crown,
  Medal,
  Activity,
  Clock,
  Smartphone,
  Monitor,
  Globe,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuth } from '@/components/auth/auth-provider'
import { ReportService, type UserStatistics } from '@/domain/report'

type DateRange = 'today' | 'yesterday' | 'week' | 'month' | 'quarter' | 'year'

export function MembersReportPage() {
  const { sessionToken } = useAuth()
  const [dateRange, setDateRange] = useState<DateRange>('month')
  const [userStats, setUserStats] = useState<UserStatistics | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const dateRangeLabels: Record<DateRange, string> = {
    today: 'วันนี้',
    yesterday: 'เมื่อวาน',
    week: '7 วันที่ผ่านมา',
    month: 'เดือนนี้',
    quarter: 'ไตรมาสนี้',
    year: 'ปีนี้',
  }

  // คำนวณ date range จาก dateRange selector
  const getDateRange = (): { start: Date; end: Date } => {
    const today = moment()
    let start: moment.Moment
    let end: moment.Moment

    switch (dateRange) {
      case 'today':
        start = today.clone().startOf('day')
        end = today.clone().endOf('day')
        break
      case 'yesterday':
        start = today.clone().subtract(1, 'day').startOf('day')
        end = today.clone().subtract(1, 'day').endOf('day')
        break
      case 'week':
        start = today.clone().subtract(7, 'days').startOf('day')
        end = today.clone().endOf('day')
        break
      case 'month':
        start = today.clone().startOf('month')
        end = today.clone().endOf('month')
        break
      case 'quarter':
        start = today.clone().startOf('quarter')
        end = today.clone().endOf('quarter')
        break
      case 'year':
        start = today.clone().startOf('year')
        end = today.clone().endOf('year')
        break
      default:
        start = today.clone().startOf('month')
        end = today.clone().endOf('month')
    }

    return {
      start: moment(start).startOf('day').utc().toDate(),
      end: moment(end).endOf('day').utc().toDate(),
    }
  }

  // Load user statistics
  const loadUserStats = async (showLoading = true) => {
    if (!sessionToken) return

    if (showLoading) setIsLoading(true)
    else setIsRefreshing(true)

    setError(null)

    try {
      const { start, end } = getDateRange()
      const stats = await ReportService.loadUserStatistics(sessionToken, start, end)
      setUserStats(stats)
    } catch (err: any) {
      console.error('Failed to load user statistics:', err)
      setError(err.message || 'ไม่สามารถโหลดข้อมูลสมาชิกได้')
    } finally {
      setIsLoading(false)
      setIsRefreshing(false)
    }
  }

  useEffect(() => {
    loadUserStats()
  }, [sessionToken, dateRange])

  // Mock data (สำหรับส่วนที่ยังไม่มี API)
  const mockStats = {
    totalMembers: 12458,
    newMembers: 156,
    activeMembers: 8234,
    inactiveMembers: 3890,
    bannedMembers: 334,
    vipMembers: 1250,
    avgDepositPerMember: 12500,
    retentionRate: 78.5,
  }

  // ใช้ข้อมูลจริง (fallback to mock)
  const summaryStats = {
    totalMembers: userStats?.totalMembers ?? mockStats.totalMembers,
    newMembers: userStats?.totalNewMembers ?? mockStats.newMembers,
    activeMembers: userStats?.activeMembers ?? mockStats.activeMembers,
    newMembersWithDeposit: userStats?.newMembersWithDeposit ?? 0,
    // ส่วนที่ยังไม่มี API ใช้ mock data
    inactiveMembers: mockStats.inactiveMembers,
    bannedMembers: mockStats.bannedMembers,
    vipMembers: mockStats.vipMembers,
    avgDepositPerMember: mockStats.avgDepositPerMember,
    retentionRate: mockStats.retentionRate,
  }

  const membersByLevel = [
    { level: 'Bronze', icon: '🥉', count: 6500, percent: 52.2, avgDeposit: 5000 },
    { level: 'Silver', icon: '🥈', count: 3200, percent: 25.7, avgDeposit: 15000 },
    { level: 'Gold', icon: '🥇', count: 1850, percent: 14.8, avgDeposit: 35000 },
    { level: 'Platinum', icon: '💎', count: 658, percent: 5.3, avgDeposit: 80000 },
    { level: 'VIP', icon: '👑', count: 250, percent: 2.0, avgDeposit: 200000 },
  ]

  const registrationByHour = [
    { hour: '00-04', count: 45 },
    { hour: '04-08', count: 28 },
    { hour: '08-12', count: 85 },
    { hour: '12-16', count: 120 },
    { hour: '16-20', count: 180 },
    { hour: '20-24', count: 210 },
  ]

  const topMembers = [
    { rank: 1, username: 'vip_whale', level: 'VIP', totalDeposit: 2850000, totalBet: 15000000, lastActive: '5 นาทีที่แล้ว' },
    { rank: 2, username: 'high_roller88', level: 'Platinum', totalDeposit: 1720000, totalBet: 8500000, lastActive: '15 นาทีที่แล้ว' },
    { rank: 3, username: 'lucky_star', level: 'Platinum', totalDeposit: 1450000, totalBet: 7200000, lastActive: '1 ชั่วโมงที่แล้ว' },
    { rank: 4, username: 'gold_member99', level: 'Gold', totalDeposit: 980000, totalBet: 4500000, lastActive: '2 ชั่วโมงที่แล้ว' },
    { rank: 5, username: 'diamond_king', level: 'Gold', totalDeposit: 850000, totalBet: 3800000, lastActive: '3 ชั่วโมงที่แล้ว' },
  ]

  const deviceStats = [
    { device: 'Mobile', icon: <Smartphone className="h-4 w-4" />, count: 8520, percent: 68.4 },
    { device: 'Desktop', icon: <Monitor className="h-4 w-4" />, count: 3150, percent: 25.3 },
    { device: 'Tablet', icon: <Monitor className="h-4 w-4" />, count: 788, percent: 6.3 },
  ]

  const activityByDay = [
    { day: 'จันทร์', active: 6500, new: 22 },
    { day: 'อังคาร', active: 6200, new: 18 },
    { day: 'พุธ', active: 6800, new: 25 },
    { day: 'พฤหัส', active: 7100, new: 28 },
    { day: 'ศุกร์', active: 7800, new: 35 },
    { day: 'เสาร์', active: 8500, new: 42 },
    { day: 'อาทิตย์', active: 8200, new: 38 },
  ]

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('th-TH').format(num)
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 0,
    }).format(amount)
  }

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'VIP': return 'text-purple-500 bg-purple-500/10'
      case 'Platinum': return 'text-cyan-500 bg-cyan-500/10'
      case 'Gold': return 'text-yellow-500 bg-yellow-500/10'
      case 'Silver': return 'text-gray-400 bg-gray-400/10'
      default: return 'text-orange-600 bg-orange-600/10'
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">รายงานสมาชิก</h1>
          <p className="text-muted-foreground">วิเคราะห์ข้อมูลและพฤติกรรมสมาชิก</p>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-2">
                <Calendar className="h-4 w-4" />
                {dateRangeLabels[dateRange]}
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {Object.entries(dateRangeLabels).map(([key, label]) => (
                <DropdownMenuItem
                  key={key}
                  onClick={() => setDateRange(key as DateRange)}
                >
                  {label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            variant="outline"
            size="icon"
            onClick={() => loadUserStats(false)}
            disabled={isRefreshing}
          >
            <RefreshCcw className={cn('h-4 w-4', isRefreshing && 'animate-spin')} />
          </Button>
          <Button variant="outline" className="gap-2">
            <Download className="h-4 w-4" />
            ส่งออก
          </Button>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <Card className="border-rose-500/50 bg-rose-500/10">
          <CardContent className="p-4">
            <p className="text-rose-600">{error}</p>
          </CardContent>
        </Card>
      )}

      {/* Main Stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {isLoading && !userStats ? (
          <>
            {Array.from({ length: 4 }).map((_, i) => (
              <Card key={i}>
                <CardContent className="p-6">
                  <Skeleton className="h-20 w-full" />
                </CardContent>
              </Card>
            ))}
          </>
        ) : (
          <>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="p-3 rounded-lg bg-primary/10">
                    <Users className="h-6 w-6 text-primary" />
                  </div>
                  <Badge variant="secondary" className="bg-win/10 text-win border-0 gap-1">
                    <TrendingUp className="h-3 w-3" />
                    +{formatNumber(summaryStats.newMembers)}
                  </Badge>
                </div>
                <div className="mt-4">
                  <p className="text-sm text-muted-foreground">สมาชิกทั้งหมด</p>
                  <p className="text-2xl font-bold">{formatNumber(summaryStats.totalMembers)}</p>
                  {userStats && (
                    <p className="text-xs text-muted-foreground mt-1">ข้อมูลจริง</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="p-3 rounded-lg bg-win/10">
                    <UserCheck className="h-6 w-6 text-win" />
                  </div>
                </div>
                <div className="mt-4">
                  <p className="text-sm text-muted-foreground">สมาชิกใช้งาน</p>
                  <p className="text-2xl font-bold text-win">{formatNumber(summaryStats.activeMembers)}</p>
                  {userStats && (
                    <p className="text-xs text-muted-foreground mt-1">ข้อมูลจริง</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="p-3 rounded-lg bg-emerald-500/10">
                    <UserPlus className="h-6 w-6 text-emerald-500" />
                  </div>
                </div>
                <div className="mt-4">
                  <p className="text-sm text-muted-foreground">สมาชิกใหม่</p>
                  <p className="text-2xl font-bold text-emerald-500">{formatNumber(summaryStats.newMembers)}</p>
                  {userStats && (
                    <p className="text-xs text-muted-foreground mt-1">ข้อมูลจริง</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="p-3 rounded-lg bg-blue-500/10">
                    <UserCheck className="h-6 w-6 text-blue-500" />
                  </div>
                </div>
                <div className="mt-4">
                  <p className="text-sm text-muted-foreground">สมาชิกใหม่ที่ฝาก</p>
                  <p className="text-2xl font-bold text-blue-500">
                    {formatNumber(summaryStats.newMembersWithDeposit)}
                  </p>
                  {userStats && (
                    <p className="text-xs text-muted-foreground mt-1">ข้อมูลจริง</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </>
        )}

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="p-3 rounded-lg bg-yellow-500/10">
                <Crown className="h-6 w-6 text-yellow-500" />
              </div>
            </div>
            <div className="mt-4">
              <p className="text-sm text-muted-foreground">สมาชิก VIP</p>
              <p className="text-2xl font-bold">{formatNumber(summaryStats.vipMembers)}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="p-3 rounded-lg bg-blue-500/10">
                <Activity className="h-6 w-6 text-blue-500" />
              </div>
            </div>
            <div className="mt-4">
              <p className="text-sm text-muted-foreground">Retention Rate</p>
              <p className="text-2xl font-bold">{summaryStats.retentionRate}%</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Member by Level & Registration Trend */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Members by Level */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Star className="h-5 w-5" />
              สมาชิกแยกตามระดับ
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {membersByLevel.map((item, idx) => (
                <div key={idx} className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{item.icon}</span>
                      <span className="font-medium">{item.level}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-muted-foreground">{formatNumber(item.count)} คน</span>
                      <span className="text-xs text-muted-foreground">
                        เฉลี่ย {formatCurrency(item.avgDeposit)}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Progress value={item.percent} className="h-2 flex-1" />
                    <span className="text-xs text-muted-foreground w-12 text-right">{item.percent}%</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Registration by Hour */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              สมัครสมาชิกรายช่วงเวลา
            </CardTitle>
            <CardDescription>จำนวนผู้สมัครใหม่วันนี้</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {registrationByHour.map((item, idx) => {
                const maxCount = Math.max(...registrationByHour.map(h => h.count))
                return (
                  <div key={idx} className="flex items-center gap-3">
                    <span className="text-sm text-muted-foreground w-16">{item.hour}</span>
                    <div className="flex-1 h-6 bg-muted/30 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-primary/60 rounded-full flex items-center justify-end pr-2"
                        style={{ width: `${(item.count / maxCount) * 100}%` }}
                      >
                        <span className="text-xs font-medium text-primary-foreground">
                          {item.count}
                        </span>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Members & Activity */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Top Members */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Medal className="h-5 w-5" />
              Top 5 สมาชิกยอดฝากสูงสุด
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {topMembers.map((member) => (
                <div key={member.rank} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "h-8 w-8 rounded-full flex items-center justify-center font-bold text-sm",
                      member.rank === 1 && "bg-yellow-500/20 text-yellow-500",
                      member.rank === 2 && "bg-gray-400/20 text-gray-400",
                      member.rank === 3 && "bg-orange-600/20 text-orange-600",
                      member.rank > 3 && "bg-muted text-muted-foreground"
                    )}>
                      {member.rank}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{member.username}</p>
                        <Badge variant="secondary" className={cn("text-xs", getLevelColor(member.level))}>
                          {member.level}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        เดิมพัน: {formatCurrency(member.totalBet)} • {member.lastActive}
                      </p>
                    </div>
                  </div>
                  <span className="font-semibold text-win">{formatCurrency(member.totalDeposit)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Device Stats */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5" />
              อุปกรณ์ที่ใช้งาน
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              {deviceStats.map((item, idx) => (
                <div key={idx} className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      {item.icon}
                      <span className="font-medium">{item.device}</span>
                    </div>
                    <span className="text-muted-foreground">{formatNumber(item.count)} คน</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Progress 
                      value={item.percent} 
                      className="h-2 flex-1"
                      indicatorClassName={cn(
                        idx === 0 && "bg-blue-500",
                        idx === 1 && "bg-purple-500",
                        idx === 2 && "bg-orange-500"
                      )}
                    />
                    <span className="text-xs text-muted-foreground w-12 text-right">{item.percent}%</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="pt-4 border-t">
              <h4 className="text-sm font-medium mb-3">สมาชิกใช้งานรายวัน</h4>
              <div className="grid grid-cols-7 gap-1">
                {activityByDay.map((item, idx) => (
                  <div key={idx} className="text-center">
                    <div className="text-xs text-muted-foreground mb-1">{item.day.slice(0, 2)}</div>
                    <div 
                      className="h-16 bg-primary/20 rounded-sm relative flex items-end justify-center"
                    >
                      <div 
                        className="bg-primary w-full rounded-sm"
                        style={{ height: `${(item.active / 8500) * 100}%` }}
                      />
                    </div>
                    <div className="text-xs mt-1 font-medium">{(item.active / 1000).toFixed(1)}k</div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}







